import { COLOR_CODES } from "../common";
import { lightenColor } from "../components/recharts/ReMixCharts";
import { ExchangeColors } from "../pages/Dashboard/Exchange3/FormatData";

/**
 * Base color palette for consistent usage
 */
export const colorList = [
  "#64B5F6", // Darker Blue
  "#FFB74D", // Darker Orange
  "#81C784", // Darker Green
  "#E57373", // Darker Red
  "#9575CD", // Darker Purple
  "#616161", // Darker Gray
  COLOR_CODES.secondary,
  COLOR_CODES.tertiary,
  lightenColor(COLOR_CODES.hpx, 60),
  lightenColor(COLOR_CODES.pixl, 20),
  COLOR_CODES.primary,
  lightenColor(COLOR_CODES.dac, 10),
  lightenColor(COLOR_CODES.hpDaily, 20),
  COLOR_CODES.hpIntraDay,
  COLOR_CODES.hpDac,
  lightenColor(COLOR_CODES.secondary, 30),
  COLOR_CODES.daily,
  lightenColor(COLOR_CODES.iex, 50),
  COLOR_CODES.largeHydro,
  COLOR_CODES.hydro,
  lightenColor(COLOR_CODES.primary, 10),
  lightenColor(COLOR_CODES.secondary, 10),
  COLOR_CODES.weightedMCP,
  lightenColor(COLOR_CODES.dac, 20),
];

/**
 * Get a color directly by index
 */
export function getRandomColor(index: number): string {
  return colorList[index % colorList.length];
}

/**
 * Map of already used colors for consistent assignment
 */
const usedColorsMap = new Map<string, string>();
let colorIndex = 0;

/**
 * Deterministic function: same string → same color (no randomness)
 */
export function stringToConsistentColor(
  str: string,
  seedOffset: number = 0
): string {
  // Already cached
  if (usedColorsMap.has(str)) {
    return usedColorsMap.get(str)!;
  }

  const combinedColorList = [...new Set([...ExchangeColors, ...colorList])];

  // Handle special cases explicitly
  const specialCases: Record<string, string> = {
    "Interstate Generation": "#64B5F6",
    "Interstate Generation Price": "#FFB74D",
  };
  if (specialCases[str]) {
    usedColorsMap.set(str, specialCases[str]);
    return specialCases[str];
  }

  // Create deterministic hash (no randomness)
  let hash = seedOffset;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Map hash to color list
  let index = Math.abs(hash) % combinedColorList.length;
  let selectedColor = combinedColorList[index];

  // Avoid duplicates: find next available color
  const usedColors = Array.from(usedColorsMap.values());
  if (usedColors.includes(selectedColor)) {
    for (let i = 0; i < combinedColorList.length; i++) {
      const altColor = combinedColorList[(index + i) % combinedColorList.length];
      if (!usedColors.includes(altColor)) {
        selectedColor = altColor;
        break;
      }
    }
    if (usedColors.includes(selectedColor)) {
      selectedColor = combinedColorList[colorIndex % combinedColorList.length];
      colorIndex++;
    }
  }

  // Store mapping
  usedColorsMap.set(str, selectedColor);
  return selectedColor;
}

/**
 * Generate color from string using HSL → Hex (algorithmic, no palette)
 */
export function stringToColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const hue = hash % 360;
  const saturation = 50 + (hash % 30); // 50%–80%
  const lightness = 35 + (hash % 25); // 35%–60%

  return hslToHex(hue, saturation, lightness);
}

/**
 * Convert HSL → Hex
 */
function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let r = 0, g = 0, b = 0;

  if (h >= 0 && h < 60) {
    r = c; g = x; b = 0;
  } else if (h >= 60 && h < 120) {
    r = x; g = c; b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0; g = c; b = x;
  } else if (h >= 180 && h < 240) {
    r = 0; g = x; b = c;
  } else if (h >= 240 && h < 300) {
    r = x; g = 0; b = c;
  } else if (h >= 300 && h < 360) {
    r = c; g = 0; b = x;
  }

  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);

  // Convert to hex and pad with zeros if necessary
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
